import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { RefreshCw, AlertTriangle, Clock, Database, Activity, Settings, History, Zap } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SyncManagementPage() {
  const [loading, setLoading] = useState(true);
  const [syncData, setSyncData] = useState(null);
  const [bumpModalOpen, setBumpModalOpen] = useState(false);
  const [forceSyncModalOpen, setForceSyncModalOpen] = useState(false);
  const [maintenanceModalOpen, setMaintenanceModalOpen] = useState(false);
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [activities, setActivities] = useState([]);

  // Bump version form state
  const [bumpForm, setBumpForm] = useState({
    contentType: 'foods',
    summary: '',
    foodsAdded: 0,
    foodsUpdated: 0,
    foodsRemoved: 0,
    recipesAdded: 0,
    recipesUpdated: 0
  });

  // Force sync form state
  const [forceSyncForm, setForceSyncForm] = useState({
    reason: '',
    expiryHours: 24
  });

  // Maintenance form state
  const [maintenanceForm, setMaintenanceForm] = useState({
    enabled: false,
    message: ''
  });

  const getToken = () => localStorage.getItem('carbiq_admin_token');

  const fetchSyncData = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/v1/admin/sync/versions`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSyncData(data);
        setMaintenanceForm({
          enabled: data.maintenanceMode,
          message: data.maintenanceMessage || ''
        });
      }
    } catch (error) {
      console.error('Error fetching sync data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchActivity = async () => {
    try {
      const response = await fetch(`${API_URL}/api/v1/admin/sync/activity?limit=50`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setActivities(data.activities || []);
      }
    } catch (error) {
      console.error('Error fetching activity:', error);
    }
  };

  useEffect(() => {
    fetchSyncData();
  }, [fetchSyncData]);

  const handleBumpVersion = async () => {
    if (!bumpForm.summary.trim()) {
      toast.error('Summary is required');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/v1/admin/sync/bump-version`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify(bumpForm)
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`${result.contentType} version updated to v${result.newVersion}`);
        setBumpModalOpen(false);
        setBumpForm({
          contentType: 'foods',
          summary: '',
          foodsAdded: 0,
          foodsUpdated: 0,
          foodsRemoved: 0,
          recipesAdded: 0,
          recipesUpdated: 0
        });
        fetchSyncData();
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (error) {
      toast.error('Failed to bump version');
    }
  };

  const handleForceSync = async () => {
    if (!forceSyncForm.reason.trim()) {
      toast.error('Reason is required');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/v1/admin/sync/force-sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify(forceSyncForm)
      });

      if (response.ok) {
        toast.success(`All mobile clients will sync within ${forceSyncForm.expiryHours} hours`);
        setForceSyncModalOpen(false);
        setForceSyncForm({ reason: '', expiryHours: 24 });
        fetchSyncData();
      }
    } catch (error) {
      toast.error('Failed to trigger force sync');
    }
  };

  const handleCancelForceSync = async () => {
    try {
      const response = await fetch(`${API_URL}/api/v1/admin/sync/force-sync`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` }
      });

      if (response.ok) {
        toast.success('Force Sync Cancelled');
        fetchSyncData();
      }
    } catch (error) {
      toast.error('Failed to cancel force sync');
    }
  };

  const handleMaintenanceToggle = async () => {
    try {
      const response = await fetch(`${API_URL}/api/v1/admin/sync/maintenance`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify(maintenanceForm)
      });

      if (response.ok) {
        toast.success(maintenanceForm.enabled ? 'Maintenance Mode Enabled' : 'Maintenance Mode Disabled');
        setMaintenanceModalOpen(false);
        fetchSyncData();
      }
    } catch (error) {
      toast.error('Failed to update maintenance mode');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sync-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Sync Management</h1>
          <p className="text-muted-foreground">Control content versioning and mobile app sync</p>
        </div>
        <Button variant="outline" onClick={fetchSyncData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Current Versions Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Current Versions
          </CardTitle>
          <CardDescription>
            Last updated: {syncData?.lastUpdatedAt ? new Date(syncData.lastUpdatedAt).toLocaleString() : 'Never'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-3xl font-bold text-primary">v{syncData?.versions?.foods || 1}</div>
              <div className="text-sm text-muted-foreground">Foods</div>
              <div className="text-xs mt-1">{syncData?.counts?.foods || 0} items</div>
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-3xl font-bold text-primary">v{syncData?.versions?.recipes || 1}</div>
              <div className="text-sm text-muted-foreground">Recipes</div>
              <div className="text-xs mt-1">{syncData?.counts?.recipes || 0} items</div>
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-3xl font-bold text-primary">v{syncData?.versions?.rescue || 1}</div>
              <div className="text-sm text-muted-foreground">Rescue</div>
              <div className="text-xs mt-1">{syncData?.counts?.rescue || 0} items</div>
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-3xl font-bold text-primary">v{syncData?.versions?.combos || 1}</div>
              <div className="text-sm text-muted-foreground">Combos</div>
              <div className="text-xs mt-1">{syncData?.counts?.combos || 0} items</div>
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-3xl font-bold text-primary">v{syncData?.versions?.tips || 1}</div>
              <div className="text-sm text-muted-foreground">Tips</div>
              <div className="text-xs mt-1">{syncData?.counts?.tips || 0} items</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => setBumpModalOpen(true)}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <RefreshCw className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold">Bump Version</h3>
                <p className="text-sm text-muted-foreground">Release content update</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => setForceSyncModalOpen(true)}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Zap className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <h3 className="font-semibold">Force Sync</h3>
                <p className="text-sm text-muted-foreground">
                  {syncData?.forceSyncRequired ? (
                    <Badge variant="destructive">Active</Badge>
                  ) : 'Trigger immediate sync'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => {
          fetchActivity();
          setActivityModalOpen(true);
        }}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Activity className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold">Sync Activity</h3>
                <p className="text-sm text-muted-foreground">{syncData?.syncStats?.last24h || 0} syncs (24h)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => setMaintenanceModalOpen(true)}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${syncData?.maintenanceMode ? 'bg-red-100' : 'bg-gray-100'}`}>
                <AlertTriangle className={`h-6 w-6 ${syncData?.maintenanceMode ? 'text-red-600' : 'text-gray-600'}`} />
              </div>
              <div>
                <h3 className="font-semibold">Maintenance Mode</h3>
                <p className="text-sm text-muted-foreground">
                  {syncData?.maintenanceMode ? (
                    <Badge variant="destructive">Enabled</Badge>
                  ) : 'Disabled'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Changelog */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Recent Changes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {syncData?.recentChangelog?.length > 0 ? (
            <div className="space-y-3">
              {syncData.recentChangelog.map((entry, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div>
                    <div className="font-medium">
                      {entry.contentType} v{entry.version}
                    </div>
                    <div className="text-sm text-muted-foreground">{entry.summary}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm">{new Date(entry.releasedAt).toLocaleDateString()}</div>
                    <div className="text-xs text-muted-foreground">by {entry.releasedBy}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">No version changes yet</p>
          )}
        </CardContent>
      </Card>

      {/* Bump Version Modal */}
      <Dialog open={bumpModalOpen} onOpenChange={setBumpModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bump Content Version</DialogTitle>
            <DialogDescription>
              Create a new version release. Mobile apps will detect this and re-sync.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Content Type</Label>
              <Select
                value={bumpForm.contentType}
                onValueChange={(v) => setBumpForm({ ...bumpForm, contentType: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="foods">Foods</SelectItem>
                  <SelectItem value="recipes">Recipes</SelectItem>
                  <SelectItem value="rescue">Rescue Actions</SelectItem>
                  <SelectItem value="combos">Smart Combos</SelectItem>
                  <SelectItem value="tips">Daily Tips</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Changes Summary *</Label>
              <Textarea
                placeholder="Describe what changed..."
                value={bumpForm.summary}
                onChange={(e) => setBumpForm({ ...bumpForm, summary: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Foods Added</Label>
                <Input
                  type="number"
                  min="0"
                  value={bumpForm.foodsAdded}
                  onChange={(e) => setBumpForm({ ...bumpForm, foodsAdded: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Foods Updated</Label>
                <Input
                  type="number"
                  min="0"
                  value={bumpForm.foodsUpdated}
                  onChange={(e) => setBumpForm({ ...bumpForm, foodsUpdated: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Foods Removed</Label>
                <Input
                  type="number"
                  min="0"
                  value={bumpForm.foodsRemoved}
                  onChange={(e) => setBumpForm({ ...bumpForm, foodsRemoved: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBumpModalOpen(false)}>Cancel</Button>
            <Button onClick={handleBumpVersion}>Confirm Version Bump</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force Sync Modal */}
      <Dialog open={forceSyncModalOpen} onOpenChange={setForceSyncModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Force Sync Broadcast</DialogTitle>
            <DialogDescription>
              Trigger immediate sync for all mobile clients. Use for urgent corrections.
            </DialogDescription>
          </DialogHeader>
          {syncData?.forceSyncRequired ? (
            <div className="space-y-4">
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="font-medium text-orange-800">Force sync is currently active</p>
                <p className="text-sm text-orange-600 mt-1">Reason: {syncData?.forceSyncReason || 'Not specified'}</p>
              </div>
              <Button variant="destructive" className="w-full" onClick={handleCancelForceSync}>
                Cancel Force Sync
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label>Reason *</Label>
                <Textarea
                  placeholder="Why is this force sync needed?"
                  value={forceSyncForm.reason}
                  onChange={(e) => setForceSyncForm({ ...forceSyncForm, reason: e.target.value })}
                />
              </div>
              <div>
                <Label>Expiry (hours)</Label>
                <Input
                  type="number"
                  min="1"
                  max="168"
                  value={forceSyncForm.expiryHours}
                  onChange={(e) => setForceSyncForm({ ...forceSyncForm, expiryHours: parseInt(e.target.value) || 24 })}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setForceSyncModalOpen(false)}>Cancel</Button>
                <Button variant="destructive" onClick={handleForceSync}>Trigger Force Sync</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Maintenance Mode Modal */}
      <Dialog open={maintenanceModalOpen} onOpenChange={setMaintenanceModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Maintenance Mode</DialogTitle>
            <DialogDescription>
              Enable to show maintenance banner to mobile users.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Enable Maintenance Mode</Label>
              <Switch
                checked={maintenanceForm.enabled}
                onCheckedChange={(checked) => setMaintenanceForm({ ...maintenanceForm, enabled: checked })}
              />
            </div>
            {maintenanceForm.enabled && (
              <div>
                <Label>Message to Users</Label>
                <Textarea
                  placeholder="Content temporarily unavailable. Using cached data."
                  value={maintenanceForm.message}
                  onChange={(e) => setMaintenanceForm({ ...maintenanceForm, message: e.target.value })}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMaintenanceModalOpen(false)}>Cancel</Button>
            <Button onClick={handleMaintenanceToggle}>
              {maintenanceForm.enabled ? 'Enable Maintenance' : 'Disable Maintenance'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity Log Modal */}
      <Dialog open={activityModalOpen} onOpenChange={setActivityModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sync Activity Log</DialogTitle>
            <DialogDescription>Recent sync requests from mobile clients</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {activities.length > 0 ? (
              activities.map((activity, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-muted rounded text-sm">
                  <div>
                    <span className="font-mono">{new Date(activity.timestamp).toLocaleString()}</span>
                    <Badge variant="outline" className="ml-2">{activity.type}</Badge>
                  </div>
                  <div className="text-muted-foreground">
                    {activity.foodsReturned} foods, {activity.recipesReturned} recipes
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-muted-foreground py-4">No sync activity recorded</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
